const STORAGE_KEY = 'ez_affiliate_attribution';
const SESSION_STORAGE_KEY = 'ez_affiliate_attribution_session';
const COOKIE_CODE_KEY = 'ez_aff';
const COOKIE_VISITOR_KEY = 'ez_afv';
const COOKIE_ATT_KEY = 'ez_att';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

type AffiliateAttribution = {
  referralCode?: string;
  visitorId?: string;
  att?: string;
};

const getCookieDomain = () => import.meta.env.VITE_AFFILIATE_ATTRIBUTION_COOKIE_DOMAIN || '';

const normalizeValue = (value: string | null | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
};

const normalizeAttribution = (input?: AffiliateAttribution | null): AffiliateAttribution => ({
  referralCode: normalizeValue(input?.referralCode),
  visitorId: normalizeValue(input?.visitorId),
  att: normalizeValue(input?.att),
});

const hasAttribution = (input?: AffiliateAttribution | null): boolean =>
  Boolean(input?.referralCode || input?.visitorId || input?.att);

const mergeAttribution = (
  ...sources: Array<AffiliateAttribution | null | undefined>
): AffiliateAttribution =>
  normalizeAttribution({
    referralCode: sources.map((source) => source?.referralCode).find(Boolean),
    visitorId: sources.map((source) => source?.visitorId).find(Boolean),
    att: sources.map((source) => source?.att).find(Boolean),
  });

const isCookieDomainCompatible = (domain: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const normalizedDomain = normalizeValue(domain)?.replace(/^\./, '').toLowerCase();
  if (!normalizedDomain) {
    return false;
  }
  const hostname = window.location.hostname.toLowerCase();
  return hostname === normalizedDomain || hostname.endsWith(`.${normalizedDomain}`);
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name: string, value: string) => {
  if (typeof document === 'undefined' || !value) {
    return;
  }
  const configuredDomain = getCookieDomain();
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ];
  if (configuredDomain && isCookieDomainCompatible(configuredDomain)) {
    parts.push(`Domain=${configuredDomain}`);
  }
  if (window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
};

const writeSessionStorage = (value: AffiliateAttribution) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!hasAttribution(value)) {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(value));
};

const readJsonStorage = (storage: Storage | undefined, key: string): AffiliateAttribution => {
  if (!storage) {
    return {};
  }
  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return {};
    }
    return normalizeAttribution(JSON.parse(raw) as AffiliateAttribution);
  } catch {
    return {};
  }
};

const readFromUrl = (): AffiliateAttribution => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  return normalizeAttribution({
    referralCode: normalizeValue(
      params.get('aff') ?? params.get('ref') ?? params.get('referralCode'),
    ),
    visitorId: normalizeValue(params.get('afv') ?? params.get('v') ?? params.get('visitorId')),
    att: normalizeValue(params.get('att') ?? params.get('attributionToken')),
  });
};

const readFromCookies = (): AffiliateAttribution =>
  normalizeAttribution({
    referralCode: normalizeValue(readCookie(COOKIE_CODE_KEY)),
    visitorId: normalizeValue(readCookie(COOKIE_VISITOR_KEY)),
    att: normalizeValue(readCookie(COOKIE_ATT_KEY)),
  });

const persistAttribution = (input: AffiliateAttribution) => {
  const normalized = normalizeAttribution(input);
  if (typeof window === 'undefined' || !hasAttribution(normalized)) {
    return normalized;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  writeSessionStorage(normalized);
  if (normalized.referralCode) {
    writeCookie(COOKIE_CODE_KEY, normalized.referralCode);
  }
  if (normalized.visitorId) {
    writeCookie(COOKIE_VISITOR_KEY, normalized.visitorId);
  }
  if (normalized.att) {
    writeCookie(COOKIE_ATT_KEY, normalized.att);
  }
  return normalized;
};

const resolveAffiliateAttribution = (): AffiliateAttribution => {
  if (typeof window === 'undefined') {
    return {};
  }

  const fromUrl = readFromUrl();
  const fromLocalStorage = readJsonStorage(window.localStorage, STORAGE_KEY);
  const fromSessionStorage = readJsonStorage(window.sessionStorage, SESSION_STORAGE_KEY);
  const fromCookies = readFromCookies();
  const resolved = mergeAttribution(fromUrl, fromLocalStorage, fromSessionStorage, fromCookies);

  if (hasAttribution(resolved)) {
    persistAttribution(resolved);
  }

  return resolved;
};

export const bootstrapAffiliateAttributionFromUrl = () => {
  resolveAffiliateAttribution();
};

const readStoredAttribution = (): AffiliateAttribution => resolveAffiliateAttribution();

export const appendAffiliateAttribution = <T extends Record<string, unknown>>(payload: T): T => {
  const { referralCode, visitorId, att } = readStoredAttribution();
  const nextPayload = { ...payload } as Record<string, unknown>;

  if (referralCode && !nextPayload.referralCode && !nextPayload.aff) {
    nextPayload.referralCode = referralCode;
    nextPayload.aff = referralCode;
  }
  if (visitorId && !nextPayload.visitorId && !nextPayload.afv) {
    nextPayload.visitorId = visitorId;
    nextPayload.afv = visitorId;
  }
  if (att && !nextPayload.att && !nextPayload.attributionToken) {
    nextPayload.att = att;
    nextPayload.attributionToken = att;
  }

  return nextPayload as T;
};

export const getAffiliateAttributionForQuery = (): {
  aff?: string;
  afv?: string;
  att?: string;
} => {
  const { referralCode, visitorId, att } = readStoredAttribution();
  return {
    aff: normalizeValue(referralCode),
    afv: normalizeValue(visitorId),
    att: normalizeValue(att),
  };
};
